'use client';

import React, { useState, ReactNode } from 'react';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Typography from '../../atoms/Typography/Typography';
import Iconography from '../../atoms/Iconography/Iconography';
import styles from './Accordion.module.scss';

/**
 * Props for the Accordion component.
 *
 * Supports both uncontrolled mode (via `defaultOpen`) and controlled mode (via `isOpen` + `onToggle`).
 * Uses CSS grid `grid-template-rows` transition for smooth expand/collapse animation
 * without any JavaScript height measurement.
 */
type AccordionProps = {
  title: string;
  children: ReactNode;
  theme?: 'light' | 'dark';
  defaultOpen?: boolean;
  /** Controlled open state. When provided, the component is fully controlled. */
  isOpen?: boolean;
  /** Callback fired when the accordion header is clicked. Receives the new open state. */
  onToggle?: (isOpen: boolean) => void;
  className?: string;
  showBorder?: boolean;
  dataTestId?: string;
};

/**
 * Collapsible accordion component with animated expand/collapse.
 *
 * @component
 * @param {AccordionProps} props - Component props
 * @returns {JSX.Element} Accordion element
 *
 * @example
 * ```tsx
 * // Uncontrolled
 * <Accordion title="Section" defaultOpen>Content</Accordion>
 *
 * // Controlled (exclusive open)
 * const { getAccordionProps } = useAccordionGroup({ defaultOpen: 'a' });
 * <Accordion title="A" {...getAccordionProps('a')}>Content A</Accordion>
 * <Accordion title="B" {...getAccordionProps('b')}>Content B</Accordion>
 * ```
 */
const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  theme = 'dark',
  defaultOpen = false,
  isOpen: controlledIsOpen,
  onToggle,
  className,
  showBorder = false,
  dataTestId,
}) => {
  const isControlled = controlledIsOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  return (
    <Box
      className={classnames(styles.accordion, { [styles.withBorder]: showBorder }, className)}
      data-theme={theme}
      data-testid={dataTestId}
    >
      <button
        className={styles.header}
        onClick={() => {
          const newState = !isOpen;
          if (!isControlled) {
            setInternalIsOpen(newState);
          }
          onToggle?.(newState);
        }}
        aria-expanded={isOpen}
        type="button"
      >
        <Typography
          variant="subtitle"
          size="small"
          theme={theme}
          color="white"
          className={styles.title}
        >
          {title}
        </Typography>
        <Iconography
          category="arrows"
          name="chevron-down"
          theme={theme}
          color="white"
          className={classnames(styles.icon, { [styles.rotated]: isOpen })}
        />
      </button>
      <div className={classnames(styles.contentWrapper, { [styles.open]: isOpen })}>
        <div className={styles.contentInner}>
          <Box className={styles.content}>{children}</Box>
        </div>
      </div>
    </Box>
  );
};

Accordion.displayName = 'Accordion';

export default Accordion;
